import React, { useEffect, useRef } from 'react';
import { Box, Typography, CircularProgress, Chip } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { Message, attachmentDisplayName } from './types';
import { tourAnchor } from '../tour/anchors';

const ERROR_COLOR = '#C23F3F';

type MessageListProps = {
  messages: Message[];
  loading: boolean;
  initialMessagesLoading: boolean;
  renderMarkdown: (text: string) => string;
};

const MessageList: React.FC<MessageListProps> = ({
  messages,
  loading,
  initialMessagesLoading,
  renderMarkdown,
}) => {
  const endRef = useRef<HTMLDivElement | null>(null);

  // `messages` is newest-first (new turns are prepended). Render oldest→newest
  // top-down so the conversation reads naturally and grows from the top instead
  // of bottom-pinning a short chat, then keep the newest turn in view.
  const ordered = [...messages]
    .map((message, i) => ({ message, index: i }))
    .reverse();

  useEffect(() => {
    endRef.current?.scrollIntoView({ block: 'end' });
  }, [messages.length, loading]);

  if (initialMessagesLoading) {
    return (
      <Box
        {...tourAnchor('chat.list')}
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress color="primary" />
      </Box>
    );
  }

  return (
    <Box
      {...tourAnchor('chat.list')}
      className="dg-chat-messages"
      sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflowY: 'auto' }}
    >
      {ordered.map(({ message, index }) => (
        <Box
          key={index}
          className={`dg-chat-bubble ${
            message.type === 'user' ? 'dg-chat-bubble--user' : 'dg-chat-bubble--bot'
          }`}
        >
          {/* File chips for user messages */}
          {message.type === 'user' &&
            message.file_ids &&
            message.file_ids.length > 0 && (
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 0.5,
                  mb: 1,
                  alignItems: 'center',
                }}
              >
                {message.file_ids.map((fileId, fileIndex) => {
                  // Prefer this file's own status; before the first per-file
                  // poll result arrives, fall back to the overall status.
                  const status =
                    message.file_statuses?.[fileId] ??
                    message.file_upload_status;

                  return (
                    <Chip
                      key={fileIndex}
                      label={attachmentDisplayName(fileId)}
                      size="small"
                      icon={
                        status === 'COMPLETED' ? (
                          <CheckCircleIcon
                            sx={{
                              color: 'green !important',
                              fontSize: '16px',
                            }}
                          />
                        ) : status === 'IN_PROGRESS' ? (
                          <CircularProgress
                            size={16}
                            sx={{ color: 'inherit' }}
                          />
                        ) : status === 'FAILED' ? (
                          <ErrorIcon
                            sx={{
                              color: `${ERROR_COLOR} !important`,
                              fontSize: '16px',
                            }}
                          />
                        ) : undefined
                      }
                      sx={{
                        backgroundColor:
                          status === 'FAILED'
                            ? `rgba(194, 63, 63, 0.1)`
                            : status === 'COMPLETED'
                              ? 'rgba(76, 175, 80, 0.1)'
                              : 'rgba(0, 0, 0, 0.05)',
                        color: message.type === 'user' ? 'black' : 'white',
                        fontSize: '0.75rem',
                        height: '24px',
                        '& .MuiChip-icon': {
                          color:
                            status === 'COMPLETED'
                              ? 'green'
                              : status === 'FAILED'
                                ? ERROR_COLOR
                                : 'inherit',
                          marginLeft: '4px',
                        },
                      }}
                    />
                  );
                })}
              </Box>
            )}
          <Box
            className="dg-chat-bubble-markdown"
            dangerouslySetInnerHTML={{
              __html: renderMarkdown(message.text),
            }}
          />
        </Box>
      ))}

      {loading && (
        <Box className="dg-chat-thinking">
          <Typography component="span">Thinking</Typography>
          <CircularProgress size={16} color="inherit" />
        </Box>
      )}

      <div ref={endRef} />
    </Box>
  );
};

export default MessageList;
